import React, { useRef, useState } from "react";
import { CustomTextField } from "../../../components/TextFields";
import { Button, FormControl, IconButton, List, ListItem } from "@mui/material";
import {
  ModalButtonContainer,
  NegativeModalButton,
  PositiveModalButton,
} from "../../../components/Button";
import { useModal } from "../../../components/modal";
import Add from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/Attachment";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import TextContainer from "../../../components/Texts/TextContainer";
import {
  AddMembersResult,
  useAddMembersMutation,
} from "../../../app/services/organ.api";
import LoadingButton from "../../../components/Button/LoadingButton";
import { normalizeIrMobile, splitPhoneList } from "../../../utils/phone";
import { readPhonesFromSheet } from "../../../utils/readPhonesFromSheet";
import { apiErrorMessage } from "../../../utils/apiError";

type ImportSummary = {
  fileName: string;
  found: number;
  added: number;
  duplicates: number;
  invalid: { row: number; value: string }[];
};

const CHUNK = 300; // keep each request small for long Excel lists

export default function AddModal({ organId }: { organId: string }) {
  const { setShow } = useModal();
  const [items, setItems] = useState<string[]>([]);
  const [currentPhone, setCurrentPhone] = useState("");
  const [err, setError] = useState<string>("");
  const [addMember, addSate] = useAddMembersMutation();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<AddMembersResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // parse the text box (one or many numbers, pasted lists too)
  const parseCurrent = (): { valid: string[]; invalid: string[] } => {
    const valid: string[] = [];
    const invalid: string[] = [];
    splitPhoneList(currentPhone).forEach((raw) => {
      const p = normalizeIrMobile(raw);
      if (p) valid.push(p);
      else invalid.push(raw);
    });
    return { valid, invalid };
  };

  const merge = (phones: string[]) =>
    setItems((prev) => Array.from(new Set([...prev, ...phones])));

  const handleAddItem = () => {
    const { valid, invalid } = parseCurrent();
    if (invalid.length) {
      setError(`شماره همراه نادرست است: ${invalid.join("، ")}`);
      return;
    }
    setError("");
    merge(valid);
    setCurrentPhone("");
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const res = await readPhonesFromSheet(file);
      const before = new Set(items);
      const fresh = res.phones.filter((p) => !before.has(p));
      merge(fresh);
      setSummary({
        fileName: file.name,
        found: res.phones.length,
        added: fresh.length,
        duplicates: res.duplicates + (res.phones.length - fresh.length),
        invalid: res.invalid,
      });
      if (!res.phones.length && !res.invalid.length)
        setError("در این فایل شماره همراهی پیدا نشد.");
    } catch (e) {
      setError("فایل خوانده نشد. یک فایل Excel (xlsx/xls) یا CSV انتخاب کنید.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    // a number typed but not added with + is still sent
    const { valid, invalid } = parseCurrent();
    if (invalid.length) {
      setError(`شماره همراه نادرست است: ${invalid.join("، ")}`);
      return;
    }
    const all = Array.from(new Set([...items, ...valid]));
    if (!all.length) {
      setError("حداقل یک شماره همراه وارد کنید یا فایل Excel بارگذاری کنید.");
      return;
    }
    setError("");
    setSending(true);
    const merged: AddMembersResult = {
      added: [],
      pending: [],
      alreadyMember: [],
      invalid: [],
    };
    try {
      for (let i = 0; i < all.length; i += CHUNK) {
        const res = await addMember({
          organId,
          data: all.slice(i, i + CHUNK),
        }).unwrap();
        merged.added.push(...(res?.added ?? []));
        merged.pending.push(...(res?.pending ?? []));
        merged.alreadyMember.push(...(res?.alreadyMember ?? []));
        merged.invalid!.push(...(res?.invalid ?? []));
      }
      setResult(merged);
    } catch (e) {
      // organ.api already shows a toast with the server message; keep it in the dialog too
      setError(apiErrorMessage(e));
      if (merged.added.length || merged.pending.length) setResult(merged);
    } finally {
      setSending(false);
    }
  };

  if (result) return <AddResult result={result} onClose={() => setShow(false)} />;
  return (
    <React.Fragment>
      <FormControl fullWidth>
        <List sx={{ width: "100%" }}>
          <ListItem sx={{ display: "flex", gap: ".5rem", alignItems: "flex-start" }}>
            <CustomTextField
              label="شماره همراه"
              placeholder="مثلاً 09123456789؛ چند شماره را با ویرگول یا خط جدید جدا کنید."
              variant="outlined"
              value={currentPhone}
              multiline
              maxRows={4}
              onChange={(e) => setCurrentPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAddItem();
                }
              }}
              error={!!err}
              style={{ margin: 0 }}
              helperText={err}
            />
            <IconButton
              sx={{
                color: "secondary.main",
                border: "1px solid white",
                borderRadius: "10px",
                width: "fit-content",
              }}
              size="large"
              title="افزودن به فهرست"
              onClick={() => handleAddItem()}
            >
              <Add />
            </IconButton>
          </ListItem>
          <ListItem sx={{ display: "flex", gap: ".75rem", flexWrap: "wrap" }}>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              variant="outlined"
              disabled={importing}
              onClick={() => fileRef.current?.click()}
              sx={{ color: "divider", borderColor: "divider", gap: ".5rem" }}
            >
              <UploadFileIcon />
              {importing ? "در حال خواندن…" : "بارگذاری از Excel / CSV"}
            </Button>
            <TextContainer color="divider" fontSize=".8rem">
              شماره‌ها در هر ستونی باشند پیدا می‌شوند؛ تکراری‌ها حذف می‌شوند.
            </TextContainer>
          </ListItem>
          {summary && (
            <ListItem sx={{ flexDirection: "column", alignItems: "flex-start", gap: ".25rem" }}>
              <TextContainer color="divider" fontSize=".85rem">
                {`«${summary.fileName}»: ${summary.found.toLocaleString("fa-IR")} شماره معتبر، ${summary.added.toLocaleString("fa-IR")} شماره به فهرست اضافه شد، ${summary.duplicates.toLocaleString("fa-IR")} تکراری.`}
              </TextContainer>
              {summary.invalid.length > 0 && (
                <TextContainer color="error.main" fontSize=".85rem">
                  {`ردیف‌های نامعتبر (${summary.invalid.length.toLocaleString("fa-IR")}): ` +
                    summary.invalid
                      .slice(0, 30)
                      .map((x) => `ردیف ${x.row}: ${x.value}`)
                      .join("، ") +
                    (summary.invalid.length > 30 ? " …" : "")}
                </TextContainer>
              )}
            </ListItem>
          )}
          {items.length > 0 && (
            <ListItem>
              <TextContainer color="divider" fontSize=".85rem">
                {`${items.length.toLocaleString("fa-IR")} شماره در فهرست`}
              </TextContainer>
            </ListItem>
          )}
          <div style={{ maxHeight: "30vh", overflow: "auto", width: "100%" }}>
            {items.map((item: string, index: number) => (
              <ListItem key={item} sx={{ gap: ".5rem" }}>
                <TextContainer color="customColors.purple">
                  {index + 1}.
                </TextContainer>
                &nbsp;
                {item}
                <IconButton
                  size="small"
                  title="حذف از فهرست"
                  onClick={() => setItems((prev) => prev.filter((x) => x !== item))}
                >
                  <RemoveCircleIcon sx={{ color: "red", fontSize: "1.1rem" }} />
                </IconButton>
              </ListItem>
            ))}
          </div>
        </List>
      </FormControl>
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
            ":hover": {
              bgcolor: "divider",
              color: "primary.main",
            },
          }}
          onClick={() => setShow(false)}
        >
          انصراف
        </Button>
        <LoadingButton
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          isLoading={addSate.isLoading || sending}
          disabled={sending || importing}
          onClick={() => submit()}
        >
          تایید
        </LoadingButton>
      </ModalButtonContainer>
    </React.Fragment>
  );
}

function ResultGroup({
  title,
  phones,
  color,
}: {
  title: string;
  phones?: string[];
  color: string;
}) {
  if (!phones?.length) return null;
  return (
    <ListItem sx={{ flexDirection: "column", alignItems: "flex-start", gap: ".25rem" }}>
      <TextContainer color={color} fontWeight="bold">
        {title} ({phones.length.toLocaleString("fa-IR")})
      </TextContainer>
      <TextContainer color="divider" direction="ltr" fontSize=".85rem">
        {phones.join("، ")}
      </TextContainer>
    </ListItem>
  );
}

function AddResult({
  result,
  onClose,
}: {
  result: AddMembersResult;
  onClose: () => void;
}) {
  const nothing =
    !result.added.length &&
    !result.pending.length &&
    !result.alreadyMember.length &&
    !result.invalid?.length;
  return (
    <React.Fragment>
      <List sx={{ width: "100%" }}>
        <ResultGroup title="عضو شدند" phones={result.added} color="success.main" />
        <ResultGroup
          title="در انتظار ثبت‌نام (پس از ثبت‌نام با همین شماره، خودکار عضو می‌شوند)"
          phones={result.pending}
          color="warning.main"
        />
        <ResultGroup
          title="از قبل عضو بودند"
          phones={result.alreadyMember}
          color="divider"
        />
        <ResultGroup
          title="نامعتبر یا متعلق به حساب سازمانی"
          phones={result.invalid}
          color="error.main"
        />
        {nothing && (
          <ListItem>
            <TextContainer color="divider">شماره‌ای ثبت نشد.</TextContainer>
          </ListItem>
        )}
      </List>
      <ModalButtonContainer>
        <Button
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          onClick={onClose}
        >
          بستن
        </Button>
      </ModalButtonContainer>
    </React.Fragment>
  );
}

// export default function AnsawersTable({
//   error,
//   helperText,
//   defaultValue,
//   onChange,
// }: {
//   error?: boolean;
//   helperText?: string;
//   defaultValue?: string[];
//   onChange: (value: string, indx: number) => void;
// }) {
//   return (

//   );
// }
